import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Heading, Layer } from "grommet";
import { Close, Chat, Shop, Schedule, Image as ImageIcon, Calendar, MailOption, Book, User, Checkmark, LinkNext } from "grommet-icons";
import "./PackageUpgradeModal.css";
import { Navigate, useNavigate } from "react-router-dom";
import { useFetchHotel, useSetCurrentUser } from "../redux/hooks";
import { getOfferCapabilities } from "../../../common/offerCapabilities";

const upgradeIcons = {
  messaging: [Chat, Schedule, ImageIcon],
  upsells: [Shop, Calendar, MailOption],
  ai: [Book, Chat, User],
  aiGuest: [Chat, Shop, User],
  automation: [Schedule, MailOption, Calendar],
};

export function PackageUpgradeModal({ onClose, capability = "ai" }) {
  const { t, i18n } = useTranslation();
  const feature = Object.hasOwn(upgradeIcons, capability) ? capability : "ai";
  const language = (i18n?.resolvedLanguage || i18n?.language || "en").startsWith("fr") ? "fr" : "en";
  const key = `offers.upgrade.${feature}`;
  const assets = `/images/upgrades/${feature}-${language}`;
  const standalonePreview = ["ai", "aiGuest", "automation"].includes(feature);
  return <Layer onEsc={onClose} onClickOutside={onClose} responsive={false}
    aria-labelledby="package-upgrade-title" aria-describedby="package-upgrade-intro"
    className="package-upgrade-layer">
    <section className={`package-upgrade package-upgrade--${feature}`}>
      <Button plain type="button" icon={<Close size="20px" />} onClick={onClose}
        aria-label={t("offers.dismiss")} className="package-upgrade-close" />
      <div className="package-upgrade-main">
        <div className="package-upgrade-copy">
          <header className="package-upgrade-header">
            <Heading id="package-upgrade-title" level={2} margin="none">{t(`${key}.title`)}</Heading>
            <p id="package-upgrade-intro">{t(`${key}.intro`)}</p>
          </header>
          <ul className="package-upgrade-benefits">
            {upgradeIcons[feature].map((Icon, index) => <li className="package-upgrade-benefit" key={index}>
              <span className="package-upgrade-icon" aria-hidden="true"><Icon size="24px" color="currentColor" /></span>
              <div><h3>{t(`${key}.benefits.${index}.title`)}</h3><p>{t(`${key}.benefits.${index}.description`)}</p></div>
            </li>)}
          </ul>
        </div>
        <figure className="package-upgrade-preview">
          <div className="package-upgrade-stage">
            <img className="package-upgrade-screen" src={`${assets}.webp`}
              alt={t(`${key}.imageAlt`)} width="760" height="660" />
            {!standalonePreview && <img className="package-upgrade-email" src={`/images/upgrades/email-${language}.webp`}
              alt={t("offers.upgrade.emailAlt")} width="600" height="740" />}
            {standalonePreview && <div className="package-upgrade-review"><Checkmark size="18px" color="currentColor" aria-hidden="true" />{t(`${key}.review`)}</div>}
          </div>
          <figcaption>{t("offers.upgrade.preview")}</figcaption>
        </figure>
      </div>
      <footer className="package-upgrade-footer">
        <div className="package-upgrade-package"><strong>{t(`${key}.package`)}</strong><p>{t(`${key}.contact`)}</p></div>
        <div className="package-upgrade-actions">
          <Button type="button" label={t("offers.notNow")} onClick={onClose} className="package-upgrade-later" />
          <Button primary label={t("offers.contact")} icon={<LinkNext size="18px" aria-hidden="true" />} reverse
            href={`mailto:info@loxe.io?subject=${encodeURIComponent(t(`${key}.subject`))}`} />
        </div>
      </footer>
    </section>
  </Layer>;
}

export function routeCapability(path) {
  if (/^(current-passcodes|unlock-records|housekeeping|gateways)(\/|$)/.test(path)) return "accessDoors";
  if (path === "settings/upsell-integrations") return "upsells";
  if (path === "settings/checkin") return "accessDoors";
  return null;
}

export default function OfferGate({ path, children }) {
  const { hotel } = useFetchHotel();
  const navigate = useNavigate();
  const { currentUser, isLoxeAdmin, isAdmin } = useSetCurrentUser();
  if (!hotel?._id) return null;
  if (path === "settings/admin" && !isLoxeAdmin) return <Navigate to="/conversations" replace />;
  if (path.startsWith("settings") && ["user", "user-multi"].includes(currentUser?.role)) return <Navigate to="/conversations" replace />;
  if (path === "settings/billing" && !isAdmin) return <Navigate to="/conversations" replace />;
  if (path === "settings/whitelabel" && !hotel.whiteLabeled) return <Navigate to="/conversations" replace />;
  const caps = getOfferCapabilities(hotel);
  const required = routeCapability(path);
  if (required && !caps[required]) {
    if (required === "upsells" || required === "ai" || required === "messaging") {
      return <PackageUpgradeModal capability={required} onClose={() => navigate("/conversations", { replace: true })} />;
    }
    return <Navigate to="/conversations" replace />;
  }
  return children;
}
